import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";

import type {
  DeletedFilter,
  PostOrder,
  PostSort,
  PostStatusFilter,
  PostTypeFilter,
} from "@/src/pages/Posts/posts.type";
import {
  isDeletedFilter,
  isPostOrder,
  isPostSort,
  isPostStatusFilter,
  isPostTypeFilter,
} from "@/src/pages/Posts/posts.type";

export type PostFilters = {
  q: string;
  status: PostStatusFilter;
  type: PostTypeFilter;
  deleted: DeletedFilter;
  sort: PostSort;
  order: PostOrder;
  page: number;
};

/**
 * Les filtres de la liste, portés par **l'URL** — même parti que les comptes et
 * les événements, et pour la même raison : on ouvre une fiche depuis cette
 * liste, et le retour arrière du navigateur démonte l'écran. Un `useState`
 * ramènerait la première page de « tout le fil » après chaque publication
 * consultée.
 *
 * ⚠️ Rien de ce qui vient de la barre d'adresse n'est cru : chaque valeur est
 * relue et bornée ici, jamais passée telle quelle à la requête.
 */
export function usePostFilters() {
  const [params, setParams] = useSearchParams();

  const filters = useMemo<PostFilters>(() => {
    const status = params.get("status");
    const type = params.get("type");
    const deleted = params.get("deleted");
    const sort = params.get("sort");
    const order = params.get("order");
    const page = Number(params.get("page"));

    const statusFilter = isPostStatusFilter(status) ? status : "all";

    return {
      q: params.get("q") ?? "",
      /*
        ⚠️ **`all` par défaut, et plus `pending_review`.** L'écran répond
        d'abord à « où en est le fil », la file de validation n'en étant qu'un
        filtre — le tableau de bord y mène d'ailleurs par
        `?status=pending_review`. Le défaut d'avant rendait invisible tout ce que
        le classifieur avait laissé passer, c'est-à-dire l'essentiel du fil.
      */
      status: statusFilter,
      type: isPostTypeFilter(type) ? type : "all",
      deleted: isDeletedFilter(deleted) ? deleted : "all",
      sort: isPostSort(sort) ? sort : "createdAt",
      /*
        ⚠️ **Le sens par défaut dépend du statut, et ce n'est pas un caprice.**
        Sur une liste générale on regarde ce qui vient d'être publié, donc du
        plus récent au plus ancien. Une **file**, elle, se relève dans l'ordre
        d'arrivée : triée à l'envers, ses dossiers les plus anciens ne sortent
        jamais. Le serveur applique la même règle par défaut (`order=asc`) ;
        elle est répétée ici parce que l'écran, lui, envoie toujours un ordre.
      */
      order: isPostOrder(order)
        ? order
        : statusFilter === "pending_review"
          ? "asc"
          : "desc",
      page: Number.isInteger(page) && page > 0 ? page : 1,
    };
  }, [params]);

  const setFilters = useCallback(
    (patch: Partial<PostFilters>) => {
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);

          for (const [cle, valeur] of Object.entries(patch)) {
            if (valeur === undefined || valeur === "") next.delete(cle);
            else next.set(cle, String(valeur));
          }

          // ⚠️ Changer un filtre ramène à la première page. Sans ça, passer de
          // « tous » (douze pages) à « refusées » (une seule) laisse la page 7
          // dans l'URL et l'écran affiche une liste vide qui ressemble à une
          // panne.
          if (!("page" in patch)) next.delete("page");

          return next;
        },
        // `replace` : parcourir une file n'a pas à remplir l'historique du
        // navigateur d'un cran par filtre essayé.
        { replace: true },
      );
    },
    [setParams],
  );

  return { filters, setFilters };
}
